import type { CrowdexCollaboration, CrowdexUIMessage } from '@crowdex/agent-contracts';
import type { ConversationPlacement } from '@crowdex/agent-contracts/api';
import type { PromptInputMessage } from '@crowdex/ui/ai/prompt-input';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import type { ChatRequestOptions } from 'ai';
import { createStore } from 'zustand/vanilla';
import type {
  ConversationDockPreview,
  ConversationLayoutNode,
} from '@/components/conversation-panel-layout';
import type {
  ExecutionMode,
  RemoteExecutionTarget,
  WorktreeStartingState,
} from '@/components/worktree-controls';
import { type AppSettings, DEFAULT_APP_SETTINGS } from '@/lib/app-settings';
import type {
  CloudRuntime,
  CloudRuntimeProvisioningPhase,
} from '@/lib/cloud-runtime/cloud-runtime';
import type { CachedConversation } from '@/lib/conversations/conversation-cache';
import type { MessageChangedFile } from '@/lib/conversations/message-changes';
import { PREVIEW_EFFORT_OPTION, PREVIEW_MODEL_OPTION } from '@/lib/desktop-preview-data';
import {
  type AppPanelTarget,
  emptyPanelLayout,
  type PanelLayout,
  shownPanelFields,
  switchPanelLayout,
} from '@/lib/desktop-shell/panel-layouts';
import type { ThreadSummaryMode } from '@/lib/desktop-shell/thread-summary-layout';
import type { ComposerElementSelection } from '@/lib/element-selection';
import type {
  BranchTarget,
  NativeAgent,
  NativeGitStatus,
  NativeWorkspaceEnvironment,
  NativeWorkspaceOpener,
} from '@/lib/native-desktop';
import type {
  ConversationPullRequestBinding,
  ConversationPullRequestClaim,
} from '@/lib/pull-request';
import type { ResponseAnnotation } from '@/lib/response-annotations';
import { type MentionTarget, mentionTargetsEqual } from '@/lib/side-panel/mention-targets';
import { NO_PANEL_TABS, type PanelTabs, showPanelView } from '@/lib/side-panel/panel-tabs';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';

/** Why a follow-up queue stopped draining, each with its own sentence in the queue. */
export type FollowUpQueuePause = 'interrupted' | 'cloud-start-failed' | 'send-failed';

/** Accepts either a new value or a functional updater, mirroring `useState`. */
export type StateSetter<T> = (value: T | ((previous: T) => T)) => void;

type ComposerResponseAnnotation = ResponseAnnotation & {
  id: string;
  kind: 'chat' | 'file' | 'plan' | 'pull_request' | 'terminal';
  source: string;
};

export type SyncedWorkspace = {
  id: string;
  logicalWorkspaceId?: string;
  /** Persisted by its owning store: the local server for this machine's paths, Crowdex Cloud for Cloud. */
  registered?: boolean;
  runtime?: 'local' | 'worktree' | 'cloud';
  machineId?: string;
  /** Whether this physical replica can be opened from the current device. */
  availableOnDevice?: boolean;
  /** Portable source identity, for example repository:github.com/acme/project. */
  syncKey?: string | null;
  rootPath: string;
  /** The server's name for it (a worktree's is its repository's). */
  name: string;
  /** For a worktree of this machine, the repository checkout it belongs to (the server's). */
  sourceRoot?: string | null;
  /** It lives on Crowdex Cloud; absent for this session's server. */
  onCloud?: true;
};

/** A conversation's own managed worktree, as the server records it. */
export type SyncedWorktree = {
  path: string;
  /** The repository checkout it is a worktree of. */
  sourceRoot: string | null;
  /** False once a cleanup took it off the disk: opening the conversation makes it again. */
  available?: boolean;
};

export type SyncedThread = {
  id: string;
  /** Explicit owner; never inferred from a filesystem path. */
  runtime: 'local' | 'worktree' | 'cloud';
  workspaceId: string;
  /** Its folder on this machine (a conversation of this server's), whether listed or not. */
  workspaceRoot?: string;
  /** Its own worktree (this server's record), kept while it continues in the main checkout. */
  worktree?: SyncedWorktree | null;
  agentConfigId?: string;
  title: string;
  collaboration?: CrowdexCollaboration;
  modelId?: string | null;
  updatedAt: string;
  lastMessageAt: string | null;
  messageRevision: number;
  /** It lives on Crowdex Cloud, reached through this server's link; absent for this server's. */
  onCloud?: true;
  /** Free labels the sidebar filters on (the plugin that started it). */
  tags?: readonly string[];
  /** Its workspace, project and parent: the sidebar groups by project and nests children. */
  placement?: ConversationPlacement;
  /** The pull request its work is on in a folder it shares, as this server records it. */
  pullRequest?: ConversationPullRequestClaim | null;
  /** Its pull requests bound by what it did (opened, printed, pushed, linked). */
  pullRequestBindings?: readonly ConversationPullRequestBinding[];
};

export type SyncedAgentConfig = { id: string; registryId: string | null; command: string };

export type QueuedFollowUp = { id: string; message: PromptInputMessage };
export type SteeredFollowUp = QueuedFollowUp & { status: 'pending' | 'accepted' };

type DraggedThread = { id: string; title: string; x: number; y: number };

type SideChatPrompt = { autoSubmit: boolean; id: string; text: string };

export type PendingAnnotation = {
  anchor: { x: number; y: number };
  /** Live DOM anchor used while the comment popover is open. */
  anchorRange?: Range;
  content: string;
  sourceId: string;
  sourceKind: 'chat' | 'file' | 'plan' | 'pull_request' | 'terminal';
  sourceLabel: string;
};

type TerminalAction = {
  id: string;
  name: string;
  command: string;
  env: Record<string, string>;
};

type BranchInWorktree = { branch: string; path: string };
/** The commits only the local branch has (`ahead`) and only the remote has (`behind`). */
export type DivergedBranch = { branch: string; ahead: number; behind: number; stash: boolean };

/** An edited message waiting for the user's choice about the files later turns changed. */
export type PendingRewind = {
  message: PromptInputMessage;
  /** The edited message: a new conversation from here copies the turns before it. */
  messageId: string;
  editedIndex: number;
  files: readonly MessageChangedFile[];
  /** The assistant turns the edit drops. */
  turnCount: number;
  /** Puts the edit back in the composer, as it was before sending. */
  cancel: () => void;
};

/**
 * A message waiting in its conversation until it can leave: its worktree made (`worktree`), its
 * cloud sandbox started (`cloud`), or its agent ready after an edit dropped the turns after it
 * (`edited`). It is only ever sent in the conversation it was written in, shown or not.
 */
export type PendingSend = {
  kind: 'worktree' | 'cloud' | 'edited';
  message: PromptInputMessage;
  options?: ChatRequestOptions;
  /**
   * Whether what it waits for is done (its worktree made, its sandbox started; an edited message
   * waits for nothing): it leaves as soon as its conversation's agent is ready.
   */
  ready: boolean;
};

/**
 * What one conversation is going through, whichever conversation is shown: work started for it
 * (a worktree, a branch switch, a cloud start) settles here, never in the conversation shown by
 * then. A conversation with nothing going on has no entry.
 */
export type ConversationState = {
  /** Its message waiting to leave (see `PendingSend`). */
  pendingSend: PendingSend | null;
  /** Its edited message waiting for the user's choice about the files later turns changed. */
  pendingRewind: PendingRewind | null;
  /** Its worktree is being made. */
  creatingWorktree: boolean;
  /** Its folder is moving between its worktree and the checkout. */
  handingOffWorktree: boolean;
  /** Its folder is switching branch. */
  switchingBranch: boolean;
  /** Its cloud repository is being prepared. */
  cloudRepositoryBusy: boolean;
  /** Without messages yet, it is being placed on its host (this server or the cloud). */
  placementPending: boolean;
  /** What its agent's session starts from, after it was handed off from another agent. */
  cloudHandoffContext: string | null;
};

export const IDLE_CONVERSATION: ConversationState = {
  pendingSend: null,
  pendingRewind: null,
  creatingWorktree: false,
  handingOffWorktree: false,
  switchingBranch: false,
  cloudRepositoryBusy: false,
  placementPending: false,
  cloudHandoffContext: null,
};

/** What `chatId` is going through. */
export function conversationStateOf(
  state: Pick<DesktopShellValues, 'conversationStates'>,
  chatId: string,
): ConversationState {
  return state.conversationStates[chatId] ?? IDLE_CONVERSATION;
}

/** What the conversation shown is going through. */
export function shownConversationState(
  state: Pick<DesktopShellValues, 'conversationStates' | 'chatId'>,
): ConversationState {
  return conversationStateOf(state, state.chatId);
}

/** The transcript a conversation's chat starts from when it is first shown. */
export type InitialTranscript = { chatId: string; messages: CrowdexUIMessage[] };

/**
 * Every piece of local component state that previously lived as `useState`
 * inside `DesktopShell`, grouped by domain. This is the single source of truth
 * for the desktop shell UI; keeping it in a store removes the render-order
 * coupling of ~90 hooks and lets child panes read state without prop drilling.
 */

/** A conversation's own changed files, and those it may share with another conversation. */
export type ConversationFiles = { own: readonly string[]; shared: readonly string[] };

const NO_CONVERSATION_FILES: ConversationFiles = { own: [], shared: [] };

export type ReviewScope = 'conversation' | 'folder';

export type DesktopShellValues = {
  // App shell + native environment
  appSettings: AppSettings;
  addRepositoryOpen: boolean;
  native: boolean | null;
  macTitleBarInset: number;
  agents: readonly NativeAgent[];
  agentId: string;
  agentDiscoveryError: string | null;
  workspace: string | null;
  personalWorkspaceRoot: string | null;
  workspaceOpeners: readonly NativeWorkspaceOpener[];
  preferredOpenerId: string | null;
  discoveringAgents: boolean;

  // Cloud runtime + handoff
  cloudRuntime: CloudRuntime | null;
  cloudProvisioningPhase: CloudRuntimeProvisioningPhase;
  cloudProvisionError: string | null;
  /**
   * The folder the cloud start was refused for because Crowdex Cloud keeps no copy of it
   * (`cloud_project_missing`): the conversation offers the ways on rather than the error. Set
   * after its `cloudProvisionError`, which clears it.
   */
  cloudProjectMissing: string | null;
  /**
   * Why the message just sent did not leave (its cloud agent could not start): it is back in the
   * composer, and the conversation says so with Retry. `null` once it is sent or the chat changes.
   */
  messageNotSent: string | null;
  localHandoffAgentId: string | null;
  localHandoffOpen: boolean;
  localHandoffWorking: boolean;
  agentHandoffError: string | null;

  // Conversation + message sync
  chatId: string;
  /** Seeds the chat of the conversation it names, and no other. */
  initialTranscript: InitialTranscript;
  /** Composer collaboration restored from a reopened conversation, applied on resume. */
  restoredCollaboration: CrowdexCollaboration | null;
  restoredModelId: string | null;
  restoredMessageCount: number | null;
  messageRevision: number;
  conversationCache: Readonly<Record<string, CachedConversation>>;
  syncedThreads: readonly SyncedThread[];
  syncedAgentConfigs: readonly SyncedAgentConfig[];
  syncedPullRequests: Readonly<Record<string, PullRequestSummary | null>>;
  workspacePullRequests: Readonly<Record<string, PullRequestSummary | null>>;
  syncedWorkspaces: readonly SyncedWorkspace[];
  workspaceSyncError: string | null;
  workspaceProvisionError: string | null;
  loadingThreadIds: ReadonlySet<string>;
  unreadThreadIds: ReadonlySet<string>;
  generatedConversationTitle: string;

  // Navigation + conversation layout
  navigationHistory: readonly string[];
  navigationIndex: number;
  splitThreadIds: readonly string[];
  conversationLayout: ConversationLayoutNode;
  draggedThread: DraggedThread | null;
  dockPreview: ConversationDockPreview | null;

  // Preview mode
  previewModel: string;
  previewEffort: string;

  // Side panel: the shown conversation's (`chatId`). Changing `chatId` parks it in
  // `panelLayouts` and shows the next conversation's (see `panel-layouts.ts`).
  sidePanelOpen: boolean;
  messageReviewDiff: string | null;
  /** The side panel's open tabs and the one it shows. */
  panelTabs: PanelTabs;
  sideChatPrompt: SideChatPrompt | null;
  selectedSubagentId: string | null;
  panelSize: number;
  /** The panel's size before it was maximized, restored when it no longer is. */
  panelRestoreSize: number;
  /** What each app tab shows: its selected item and the route of its view (`#/runs/<id>`). */
  appPanelTargets: Readonly<Record<string, AppPanelTarget>>;
  /** The tabs shown side by side in the panel (see `panel-split.ts`). */
  panelSplit: ConversationLayoutNode | null;
  /** The panels of the conversations not shown, by conversation id. */
  panelLayouts: Readonly<Record<string, PanelLayout>>;

  // The conversation's summary: pinned beside the transcript, or a popover when it has no room.
  summaryPinned: boolean;
  summaryPopoverOpen: boolean;
  /** How the open conversation shows its summary; null while no transcript is shown. */
  summaryMode: ThreadSummaryMode | null;
  /** The composer's model popover (D8), also opened from the header's usage ring. */
  modelMenuOpen: boolean;

  // Composer + follow-ups + annotations
  queuedFollowUps: Readonly<Record<string, readonly QueuedFollowUp[]>>;
  steeredFollowUps: Readonly<Record<string, readonly SteeredFollowUp[]>>;
  /** Why a conversation's queue stopped draining; absent while it drains. */
  pausedFollowUpQueues: Readonly<Record<string, FollowUpQueuePause | undefined>>;
  paneDrafts: Readonly<Record<string, string>>;
  /** Bumped to put the caret at the end of the active composer (a draft given from elsewhere). */
  composerFocusRequest: number;
  editingMessageId: string | null;
  editingMessageContainer: HTMLDivElement | null;
  /** What each conversation is going through, by conversation id (see `ConversationState`). */
  conversationStates: Readonly<Record<string, ConversationState>>;
  /**
   * The files the open conversation's turns changed, from their checkpoints: its own, and those
   * another conversation in the same folder may have changed too. Commits and Review scope to
   * them.
   */
  conversationFiles: ConversationFiles;
  /** Review's uncommitted changes: the conversation's own (by default), or the whole folder's. */
  reviewScope: ReviewScope;
  elementSelections: ComposerElementSelection[];
  responseAnnotations: ComposerResponseAnnotation[];
  pendingAnnotation: PendingAnnotation | null;
  pendingAnnotationText: string;

  // Git + worktree
  gitStatus: NativeGitStatus | null;
  gitStatusState:
    | 'idle'
    | 'loading'
    | 'ready'
    | 'unavailable'
    | 'not-repository'
    | 'folder-missing';
  localBranches: readonly string[];
  cloudPullRequest: PullRequestSummary | null;
  executionMode: ExecutionMode;
  remoteExecutionTarget: RemoteExecutionTarget | null;
  worktreeStartingState: WorktreeStartingState;
  workspaceEnvironment: NativeWorkspaceEnvironment | null;
  terminalAction: TerminalAction | null;
  agentTerminalRequestId: string | null;
  createBranchOpen: boolean;
  newBranchName: string;
  branchError: string | null;
  pendingBranchSwitch: BranchTarget | null;
  /** A remote branch whose local namesake has commits of its own, waiting for the user's choice. */
  divergedBranch: DivergedBranch | null;
  branchInWorktree: BranchInWorktree | null;

  // Composer references
  panelMentionTargets: readonly MentionTarget[];
};

type DesktopShellActions = {
  setAppSettings: StateSetter<AppSettings>;
  setAddRepositoryOpen: StateSetter<boolean>;
  setNative: StateSetter<boolean | null>;
  setMacTitleBarInset: StateSetter<number>;
  setAgents: StateSetter<readonly NativeAgent[]>;
  setAgentId: StateSetter<string>;
  setAgentDiscoveryError: StateSetter<string | null>;
  setWorkspace: StateSetter<string | null>;
  setPersonalWorkspaceRoot: StateSetter<string | null>;
  setWorkspaceOpeners: StateSetter<readonly NativeWorkspaceOpener[]>;
  setPreferredOpenerId: StateSetter<string | null>;
  setDiscoveringAgents: StateSetter<boolean>;
  setCloudRuntime: StateSetter<CloudRuntime | null>;
  setCloudProvisioningPhase: StateSetter<CloudRuntimeProvisioningPhase>;
  setCloudProvisionError: StateSetter<string | null>;
  setCloudProjectMissing: StateSetter<string | null>;
  setMessageNotSent: StateSetter<string | null>;
  setLocalHandoffAgentId: StateSetter<string | null>;
  setLocalHandoffOpen: StateSetter<boolean>;
  setLocalHandoffWorking: StateSetter<boolean>;
  setAgentHandoffError: StateSetter<string | null>;
  setChatId: StateSetter<string>;
  setInitialMessages: (chatId: string, messages: CrowdexUIMessage[]) => void;
  setRestoredCollaboration: StateSetter<CrowdexCollaboration | null>;
  setRestoredModelId: StateSetter<string | null>;
  setRestoredMessageCount: StateSetter<number | null>;
  setMessageRevision: StateSetter<number>;
  setConversationCache: StateSetter<Readonly<Record<string, CachedConversation>>>;
  setSyncedThreads: StateSetter<readonly SyncedThread[]>;
  setSyncedAgentConfigs: StateSetter<readonly SyncedAgentConfig[]>;
  setSyncedPullRequests: StateSetter<Readonly<Record<string, PullRequestSummary | null>>>;
  setWorkspacePullRequests: StateSetter<Readonly<Record<string, PullRequestSummary | null>>>;
  setSyncedWorkspaces: StateSetter<readonly SyncedWorkspace[]>;
  setWorkspaceSyncError: StateSetter<string | null>;
  setWorkspaceProvisionError: StateSetter<string | null>;
  setLoadingThreadIds: StateSetter<ReadonlySet<string>>;
  setUnreadThreadIds: StateSetter<ReadonlySet<string>>;
  setGeneratedConversationTitle: StateSetter<string>;
  setNavigationHistory: StateSetter<readonly string[]>;
  setNavigationIndex: StateSetter<number>;
  setSplitThreadIds: StateSetter<readonly string[]>;
  setConversationLayout: StateSetter<ConversationLayoutNode>;
  setDraggedThread: StateSetter<DraggedThread | null>;
  setDockPreview: StateSetter<ConversationDockPreview | null>;
  setPreviewModel: StateSetter<string>;
  setPreviewEffort: StateSetter<string>;
  setSidePanelOpen: StateSetter<boolean>;
  setMessageReviewDiff: StateSetter<string | null>;
  setPanelTabs: StateSetter<PanelTabs>;
  setPanelSplit: StateSetter<ConversationLayoutNode | null>;
  setSideChatPrompt: StateSetter<SideChatPrompt | null>;
  setSelectedSubagentId: StateSetter<string | null>;
  setPanelSize: StateSetter<number>;
  setPanelRestoreSize: StateSetter<number>;
  setAppPanelTargets: StateSetter<Readonly<Record<string, AppPanelTarget>>>;
  /** The panels of the conversations not shown (a tab prepared in one, `dockAppIn`). */
  setPanelLayouts: StateSetter<Readonly<Record<string, PanelLayout>>>;
  setSummaryPinned: StateSetter<boolean>;
  setSummaryPopoverOpen: StateSetter<boolean>;
  setSummaryMode: StateSetter<ThreadSummaryMode | null>;
  setModelMenuOpen: StateSetter<boolean>;
  setQueuedFollowUps: StateSetter<Readonly<Record<string, readonly QueuedFollowUp[]>>>;
  setSteeredFollowUps: StateSetter<Readonly<Record<string, readonly SteeredFollowUp[]>>>;
  setPausedFollowUpQueues: StateSetter<Readonly<Record<string, FollowUpQueuePause | undefined>>>;
  setPaneDrafts: StateSetter<Readonly<Record<string, string>>>;
  setComposerFocusRequest: StateSetter<number>;
  setEditingMessageId: StateSetter<string | null>;
  setEditingMessageContainer: StateSetter<HTMLDivElement | null>;
  /**
   * Changes what `chatId` is going through. Code that started work for a conversation writes
   * through its scope (`ConversationScope.update`), which names it.
   */
  setConversationState: (
    chatId: string,
    update:
      | Partial<ConversationState>
      | ((current: ConversationState) => Partial<ConversationState>),
  ) => void;
  setConversationFiles: StateSetter<ConversationFiles>;
  setReviewScope: StateSetter<ReviewScope>;
  setElementSelections: StateSetter<ComposerElementSelection[]>;
  setResponseAnnotations: StateSetter<ComposerResponseAnnotation[]>;
  setPendingAnnotation: StateSetter<PendingAnnotation | null>;
  setPendingAnnotationText: StateSetter<string>;
  setGitStatus: StateSetter<NativeGitStatus | null>;
  setGitStatusState: StateSetter<DesktopShellValues['gitStatusState']>;
  setLocalBranches: StateSetter<readonly string[]>;
  setCloudPullRequest: StateSetter<PullRequestSummary | null>;
  setExecutionMode: StateSetter<ExecutionMode>;
  setRemoteExecutionTarget: StateSetter<RemoteExecutionTarget | null>;
  setWorktreeStartingState: StateSetter<WorktreeStartingState>;
  setWorkspaceEnvironment: StateSetter<NativeWorkspaceEnvironment | null>;
  setTerminalAction: StateSetter<TerminalAction | null>;
  setAgentTerminalRequestId: StateSetter<string | null>;
  setCreateBranchOpen: StateSetter<boolean>;
  setNewBranchName: StateSetter<string>;
  setBranchError: StateSetter<string | null>;
  setPendingBranchSwitch: StateSetter<BranchTarget | null>;
  setDivergedBranch: StateSetter<DivergedBranch | null>;
  setBranchInWorktree: StateSetter<BranchInWorktree | null>;
  setPanelMentionTargets: StateSetter<readonly MentionTarget[]>;
};

export type DesktopShellState = DesktopShellValues & DesktopShellActions;

export type { AppPanelTarget, PanelLayout } from '@/lib/desktop-shell/panel-layouts';

export type CreateDesktopShellStoreOptions = {
  initialChatId?: string;
  initialSidePanelOpen?: boolean;
  initialSidePanelView?: SidePanelView;
  initialWorkspace?: string;
  /** The conversations' panels remembered from a previous run. */
  initialPanelLayouts?: Readonly<Record<string, PanelLayout>>;
};

export function createDesktopShellStore(options: CreateDesktopShellStoreOptions = {}) {
  const chatId = options.initialChatId ?? crypto.randomUUID();
  const { [chatId]: rememberedPanel, ...parkedPanels } = options.initialPanelLayouts ?? {};
  const initialPanel = shownPanelFields(
    options.initialSidePanelView === undefined
      ? (rememberedPanel ?? emptyPanelLayout())
      : {
          ...emptyPanelLayout(),
          open: options.initialSidePanelOpen ?? false,
          tabs: showPanelView(NO_PANEL_TABS, options.initialSidePanelView),
        },
  );
  return createStore<DesktopShellState>()((set) => {
    const make =
      <K extends keyof DesktopShellValues>(
        key: K,
        equal: (left: DesktopShellValues[K], right: DesktopShellValues[K]) => boolean = Object.is,
      ): StateSetter<DesktopShellValues[K]> =>
      (value) =>
        set((state) => {
          const previous = state[key];
          const next =
            typeof value === 'function'
              ? (value as (previous: DesktopShellValues[K]) => DesktopShellValues[K])(previous)
              : value;
          return equal(previous, next) ? state : ({ [key]: next } as Pick<DesktopShellValues, K>);
        });
    return {
      appSettings: DEFAULT_APP_SETTINGS,
      addRepositoryOpen: false,
      native: null,
      macTitleBarInset: 0,
      agents: [],
      agentId: '',
      agentDiscoveryError: null,
      workspace: options.initialWorkspace ?? null,
      personalWorkspaceRoot: null,
      workspaceOpeners: [],
      preferredOpenerId: null,
      discoveringAgents: true,
      cloudRuntime: null,
      cloudProvisioningPhase: 'task-configuration',
      cloudProvisionError: null,
      cloudProjectMissing: null,
      messageNotSent: null,
      localHandoffAgentId: null,
      localHandoffOpen: false,
      localHandoffWorking: false,
      agentHandoffError: null,
      chatId,
      initialTranscript: { chatId, messages: [] },
      restoredCollaboration: null,
      restoredModelId: null,
      restoredMessageCount: null,
      messageRevision: 0,
      conversationCache: {},
      syncedThreads: [],
      syncedAgentConfigs: [],
      syncedPullRequests: {},
      workspacePullRequests: {},
      syncedWorkspaces: [],
      workspaceSyncError: null,
      workspaceProvisionError: null,
      loadingThreadIds: new Set<string>(),
      unreadThreadIds: new Set<string>(),
      generatedConversationTitle: 'New conversation',
      navigationHistory: [chatId],
      navigationIndex: 0,
      splitThreadIds: [],
      conversationLayout: { kind: 'pane', id: chatId },
      draggedThread: null,
      dockPreview: null,
      previewModel: PREVIEW_MODEL_OPTION.currentValue,
      previewEffort: PREVIEW_EFFORT_OPTION.currentValue,
      ...initialPanel,
      sideChatPrompt: null,
      panelLayouts: parkedPanels,
      summaryPinned: true,
      summaryPopoverOpen: false,
      summaryMode: null,
      modelMenuOpen: false,
      queuedFollowUps: {},
      steeredFollowUps: {},
      pausedFollowUpQueues: {},
      paneDrafts: {},
      composerFocusRequest: 0,
      editingMessageId: null,
      editingMessageContainer: null,
      conversationStates: {},
      conversationFiles: NO_CONVERSATION_FILES,
      reviewScope: 'conversation',
      elementSelections: [],
      responseAnnotations: [],
      pendingAnnotation: null,
      pendingAnnotationText: '',
      gitStatus: null,
      gitStatusState: 'idle',
      localBranches: [],
      cloudPullRequest: null,
      executionMode: 'local',
      remoteExecutionTarget: null,
      worktreeStartingState: { type: 'working-tree' },
      workspaceEnvironment: null,
      terminalAction: null,
      agentTerminalRequestId: null,
      createBranchOpen: false,
      newBranchName: '',
      branchError: null,
      pendingBranchSwitch: null,
      divergedBranch: null,
      branchInWorktree: null,
      panelMentionTargets: [],
      setAppSettings: make('appSettings'),
      setAddRepositoryOpen: make('addRepositoryOpen'),
      setNative: make('native'),
      setMacTitleBarInset: make('macTitleBarInset'),
      setAgents: make('agents'),
      setAgentId: make('agentId'),
      setAgentDiscoveryError: make('agentDiscoveryError'),
      setWorkspace: make('workspace'),
      setPersonalWorkspaceRoot: make('personalWorkspaceRoot'),
      setWorkspaceOpeners: make('workspaceOpeners'),
      setPreferredOpenerId: make('preferredOpenerId'),
      setDiscoveringAgents: make('discoveringAgents'),
      setCloudRuntime: make('cloudRuntime'),
      setCloudProvisioningPhase: make('cloudProvisioningPhase'),
      // Another failure (or none) is not the missing project's.
      setCloudProvisionError: (value) =>
        set((state) => {
          const next = typeof value === 'function' ? value(state.cloudProvisionError) : value;
          return next === state.cloudProvisionError && state.cloudProjectMissing === null
            ? state
            : { cloudProvisionError: next, cloudProjectMissing: null };
        }),
      setCloudProjectMissing: make('cloudProjectMissing'),
      setMessageNotSent: make('messageNotSent'),
      setLocalHandoffAgentId: make('localHandoffAgentId'),
      setLocalHandoffOpen: make('localHandoffOpen'),
      setLocalHandoffWorking: make('localHandoffWorking'),
      setAgentHandoffError: make('agentHandoffError'),
      setChatId: (value) =>
        set((state) => {
          const next = typeof value === 'function' ? value(state.chatId) : value;
          if (next === state.chatId) return state;
          return { chatId: next, ...switchPanelLayout(state, state.chatId, next) };
        }),
      setInitialMessages: (target, messages) =>
        set({ initialTranscript: { chatId: target, messages } }),
      setRestoredCollaboration: make('restoredCollaboration'),
      setRestoredModelId: make('restoredModelId'),
      setRestoredMessageCount: make('restoredMessageCount'),
      setMessageRevision: make('messageRevision'),
      setConversationCache: make('conversationCache'),
      setSyncedThreads: make('syncedThreads'),
      setSyncedAgentConfigs: make('syncedAgentConfigs'),
      setSyncedPullRequests: make('syncedPullRequests'),
      setWorkspacePullRequests: make('workspacePullRequests'),
      setSyncedWorkspaces: make('syncedWorkspaces'),
      setWorkspaceSyncError: make('workspaceSyncError'),
      setWorkspaceProvisionError: make('workspaceProvisionError'),
      setLoadingThreadIds: make('loadingThreadIds'),
      setUnreadThreadIds: make('unreadThreadIds'),
      setGeneratedConversationTitle: make('generatedConversationTitle'),
      setNavigationHistory: make('navigationHistory'),
      setNavigationIndex: make('navigationIndex'),
      setSplitThreadIds: make('splitThreadIds'),
      setConversationLayout: make('conversationLayout'),
      setDraggedThread: make('draggedThread'),
      setDockPreview: make('dockPreview'),
      setPreviewModel: make('previewModel'),
      setPreviewEffort: make('previewEffort'),
      setSidePanelOpen: make('sidePanelOpen'),
      setMessageReviewDiff: make('messageReviewDiff'),
      setPanelTabs: make('panelTabs'),
      setPanelSplit: make('panelSplit'),
      setSideChatPrompt: make('sideChatPrompt'),
      setSelectedSubagentId: make('selectedSubagentId'),
      setPanelSize: make('panelSize'),
      setPanelRestoreSize: make('panelRestoreSize'),
      setAppPanelTargets: make('appPanelTargets'),
      setPanelLayouts: make('panelLayouts'),
      setSummaryPinned: make('summaryPinned'),
      setSummaryPopoverOpen: make('summaryPopoverOpen'),
      setSummaryMode: make('summaryMode'),
      setModelMenuOpen: make('modelMenuOpen'),
      setQueuedFollowUps: make('queuedFollowUps'),
      setSteeredFollowUps: make('steeredFollowUps'),
      setPausedFollowUpQueues: make('pausedFollowUpQueues'),
      setPaneDrafts: make('paneDrafts'),
      setComposerFocusRequest: make('composerFocusRequest'),
      setEditingMessageId: make('editingMessageId'),
      setEditingMessageContainer: make('editingMessageContainer'),
      setConversationState: (target, update) =>
        set((state) => {
          const current = conversationStateOf(state, target);
          const next = {
            ...current,
            ...(typeof update === 'function' ? update(current) : update),
          };
          const keys = Object.keys(IDLE_CONVERSATION) as (keyof ConversationState)[];
          if (keys.every((key) => next[key] === current[key])) return state;
          const { [target]: _previous, ...others } = state.conversationStates;
          return {
            conversationStates: keys.every((key) => next[key] === IDLE_CONVERSATION[key])
              ? others
              : { ...others, [target]: next },
          };
        }),
      setConversationFiles: make('conversationFiles'),
      setReviewScope: make('reviewScope'),
      setElementSelections: make('elementSelections'),
      setResponseAnnotations: make('responseAnnotations'),
      setPendingAnnotation: make('pendingAnnotation'),
      setPendingAnnotationText: make('pendingAnnotationText'),
      setGitStatus: make('gitStatus'),
      setGitStatusState: make('gitStatusState'),
      setLocalBranches: make('localBranches'),
      setCloudPullRequest: make('cloudPullRequest'),
      setExecutionMode: make('executionMode'),
      setRemoteExecutionTarget: make('remoteExecutionTarget'),
      setWorktreeStartingState: make('worktreeStartingState'),
      setWorkspaceEnvironment: make('workspaceEnvironment'),
      setTerminalAction: make('terminalAction'),
      setAgentTerminalRequestId: make('agentTerminalRequestId'),
      setCreateBranchOpen: make('createBranchOpen'),
      setNewBranchName: make('newBranchName'),
      setBranchError: make('branchError'),
      setPendingBranchSwitch: make('pendingBranchSwitch'),
      setDivergedBranch: make('divergedBranch'),
      setBranchInWorktree: make('branchInWorktree'),
      setPanelMentionTargets: make('panelMentionTargets', mentionTargetsEqual),
    };
  });
}

export type DesktopShellStore = ReturnType<typeof createDesktopShellStore>;
